import { apiClient } from '../infra/http/apiClient'

class TripService {
  /**
   * Get all trips
   * @param {Object} filters - Optional filters
   * @param {string} [filters.status] - Filter by status (DRAFT, PUBLISHED, CANCELLED, COMPLETED)
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object containing trip list
   */
  static async getTrips(filters = {}) {
    try {
      const response = await apiClient.get('/trips', {
        params: filters,
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch trips:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get single trip details
   * @param {string} tripId - Trip ID
   * @returns {Promise} Promise object containing trip details
   */
  static async getTrip(tripId) {
    try {
      const response = await apiClient.get(`/trip/${tripId}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch trip details:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Create new trip
   * @param {Object} tripData - Trip data
   * @param {string} tripData.title - Trip title
   * @param {string[]} [tripData.highlights] - Trip highlights list
   * @param {string} tripData.description - Trip description
   * @param {string} [tripData.details] - Detailed information (markdown format)
   * @param {string} tripData.skiResort - Ski resort
   * @param {string} tripData.location - Detailed location
   * @param {string} tripData.date - Date
   * @param {string} [tripData.registrationDeadline] - Registration deadline
   * @param {string} [tripData.cancellationDeadline] - Cancellation deadline
   * @param {number} [tripData.minParticipants] - Minimum participants
   * @param {string} [tripData.image] - Image URL
   * @param {boolean} [tripData.equipment] - Equipment rental available
   * @param {string} [tripData.status] - Status
   * @returns {Promise} Promise object containing created trip
   */
  static async createTrip(tripData) {
    try {
      const response = await apiClient.post('/trips', tripData)
      return response.data
    } catch (error) {
      console.error('Failed to create trip:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update trip information
   * @param {string} tripId - Trip ID
   * @param {Object} tripData - Trip data to update
   * @returns {Promise} Promise object containing updated trip
   */
  static async updateTrip(tripId, tripData) {
    try {
      const response = await apiClient.put(`/trip/${tripId}`, tripData)
      return response.data
    } catch (error) {
      console.error('Failed to update trip:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete trip
   * @param {string} tripId - Trip ID to delete
   * @returns {Promise} Promise object containing deletion result
   */
  static async deleteTrip(tripId) {
    try {
      const response = await apiClient.delete(`/trip/${tripId}`)
      return response.data
    } catch (error) {
      console.error('Failed to delete trip:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get all registrations for a trip
   * @param {string} tripId - Trip ID
   * @param {Object} filters - Optional filters
   * @param {string} [filters.status] - Filter by status (PENDING, CONFIRMED, CANCELLED, WAITLISTED)
   * @returns {Promise} Promise object containing registration list
   */
  static async getTripRegistrations(tripId, filters = {}) {
    try {
      const response = await apiClient.get(`/trip/${tripId}/registrations`, {
        params: filters,
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch registrations:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Register for a trip
   * @param {string} tripId - Trip ID
   * @param {Object} registrationData - Registration data
   * @param {string} registrationData.userId - User ID
   * @param {string} [registrationData.notes] - Notes
   * @returns {Promise} Promise object containing registration result
   */
  static async registerTrip(tripId, registrationData) {
    try {
      const response = await apiClient.post(`/trip/${tripId}/registrations`, registrationData)
      return response.data
    } catch (error) {
      console.error('Failed to register for trip:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update registration information
   * @param {string} tripId - Trip ID
   * @param {string} registrationId - Registration ID
   * @param {Object} updateData - Update data
   * @returns {Promise} Promise object containing updated registration
   */
  static async updateRegistration(tripId, registrationId, updateData) {
    try {
      const response = await apiClient.put(`/trip/${tripId}/registration/${registrationId}`, updateData)
      return response.data
    } catch (error) {
      console.error('Failed to update registration:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Cancel registration
   * @param {string} tripId - Trip ID
   * @param {string} registrationId - Registration ID
   * @returns {Promise} Promise object containing cancellation result
   */
  static async cancelRegistration(tripId, registrationId) {
    try {
      const response = await apiClient.delete(`/trip/${tripId}/registration/${registrationId}`)
      return response.data
    } catch (error) {
      console.error('Failed to cancel registration:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request, please check your input.')
        case 404:
          return new Error('Trip not found.')
        case 409:
          return new Error(data.message || 'Trip is full or already registered.')
        case 401:
          return new Error('Please login to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 503:
          if (data?.code === 'MAINTENANCE_MODE') {
            return new Error(data.message || 'The system is currently under maintenance. Please try again later.')
          }
          return new Error(data.message || 'Service temporarily unavailable. Please try again later.')
        default:
          return new Error('An unexpected error occurred. Please try again later.')
      }
    }
    return new Error('Unable to connect to server. Please check your network connection.')
  }
}

export default TripService
